<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useToast } from 'primevue/usetoast';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import AddManageTourGatewayDestination from '@/components/modal/AddManageTourGatewayDestination.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';
import TourGatewayDestinationList from '@/components/tour/TourGatewayDestinationList.vue';
import ViewDataTourGateDesModal from '@/components/modal/tour/ViewDataTourGateDesModal.vue';
import VersionRevertTourGateDes from '@/components/tour/VersionRevertTourGateDes.vue';

const tourManagementStore = useTourManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const toast = useToast();
const tourGatewayDestination = ref([]);
const tourGatewayDestDataDefault = ref([]);
const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const isLoading = ref(false);
const isProgress = ref(false);
const isShowModal = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);
const urlUpload = computed(() => tourManagementStore.getUrlUploadFile);

const getTourGateway = async () => {
  isLoading.value = true;
  tourManagementStore
    .getTourGateway()
    .then((res: any) => {
      if (res.data?.length) {
        const listTourOnly = res.data.filter((tour: any) => tour.isTourOnly);
        tourGatewayDestDataDefault.value = res.data;
        tourGatewayDestination.value = listTourOnly;
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getTourGateway();

const addTourGateway = async (tourData: any) => {
  const payload = tourData;
  isLoading.value = true;
  tourManagementStore
    .addTourGateway(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.submit_success'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
      getTourGateway();
    });
};

const openAddTourModal = () => {
  const dialogRef = dialog.open(AddManageTourGatewayDestination, {
    props: {
      header: t('management.tour.manage_tour_gate_dest'),
      modal: true,
      style: {
        width: '85vw'
      }
    },
    data: {
      isEdit: false
    },
    emits: {
      onApply: (data: any) => {
        addTourGateway(data);
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const editTourGateway = async (tourData: any) => {
  const payload = tourData;
  isLoading.value = true;
  tourManagementStore
    .editTourGateway(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.submit_success'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
      getTourGateway();
    });
};

const openEditTourModal = (editData: any) => {
  const dialogRef = dialog.open(AddManageTourGatewayDestination, {
    props: {
      header: t('management.tour.manage_tour_gate_dest'),
      modal: true,
      style: {
        width: '85vw'
      }
    },
    data: {
      isEdit: true,
      tourGatewayDestData: editData
    },
    emits: {
      onApply: (data: any) => {
        editTourGateway(data);
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const deleteTourGateway = (deletePayload: any) => {
  isLoading.value = true;
  tourManagementStore
    .deleteTourGateway(deletePayload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
      getTourGateway();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.delete_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleApplyImportFile = (file: any) => {
  isProgress.value = true;
  isShowModal.value = true;
  isRevert.value = false;
  const payload = new FormData();
  payload.append('file', file);
  tourManagementStore
    .importTourGateDes(payload)
    .then(() => {
      isSuccess.value = true;
      getTourGateway();
    })
    .catch(() => {
      isSuccess.value = false;
    })
    .finally(() => {
      isProgress.value = false;
    });
};

const handleApplyRevertFile = (file: any) => {
  isRevert.value = true;
  isProgress.value = true;
  isShowModal.value = true;
  tourManagementStore
    .revertFileTourGateDes(file)
    .then(() => {
      isSuccess.value = true;
      getTourGateway();
    })
    .catch(() => {
      isSuccess.value = false;
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isProgress.value = false;
    });
};

const handlePreviewFile = ({ tourGatewayDestList, file, type }: any) => {
  dialog.open(ViewDataTourGateDesModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      tourGatewayDestList: tourGatewayDestList,
      type: type
    },
    emits: {
      onApply: () => {
        if (type === 'IMPORT') {
          handleApplyImportFile(file);
        } else if (type === 'REVERT') {
          handleApplyRevertFile(file);
        }
      },
      handleReOpenModal: () => {
        handleShowRevertVersion();
      }
    }
  });
};

const openModalRevertFile = (versions: any) => {
  dialog.open(VersionRevertTourGateDes, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onSelectVersion: (data: any) => {
        handlePreviewFile(data);
      }
    }
  });
};

const handleShowRevertVersion = () => {
  isLoading.value = true;
  tourManagementStore
    .getVersionRevertTourGateDes('TOUR_COMBINATION')
    .then((response) => {
      openModalRevertFile(response.data);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleCloseModalProgress = (value: boolean) => {
  if (isRevert.value) {
    handleShowRevertVersion();
  }
  isShowModal.value = value;
};

defineExpose({
  getTourGateway
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="gate-dest-tour">
    <div class="w-full flex justify-content-end mb-4">
      <Button v-if="authorizations.tour_combination_configuration.add_tour_combination_manually" id="add-manual-tour-btn" icon="pi pi-plus" :label="$t('management.tour.add_manual_tour_detail')" @click="openAddTourModal()"></Button>
    </div>
    <TourGatewayDestinationList
      :tourGatewayDestData="tourGatewayDestination"
      :tourGatewayDestDataDefault="tourGatewayDestDataDefault"
      :urlUpload="urlUpload"
      @handlePreviewFile="(data) => handlePreviewFile(data)"
      @handleRemoveTourGateway="deleteTourGateway($event)"
      @openEditTourModal="openEditTourModal($event)"
      @handleShowRevertVersion="handleShowRevertVersion()"
    ></TourGatewayDestinationList>
  </div>
  <Dialog v-model:visible="isShowModal" :header="$t('management.tour.uploading_tour_gateway_destination')" modal :closable="!isProgress">
    <ProgressModal :isProgress="isProgress" @handleClose="(value) => handleCloseModalProgress(value)" :isSuccess="isSuccess" :isRevert="isRevert" />
  </Dialog>
</template>
